// Rebindable single-key shortcuts. The behaviour of each action lives in main.ts
// (it needs setTopMode / setShapeMode / …); this module owns only the key↔action
// mapping: the defaults, the user's overrides (persisted to localStorage), and
// the lookups the keymap + Settings overlay share. Keys are stored lower-cased,
// and kept unique — binding a key already in use unbinds the previous owner.

// Which app mode an action belongs to. Model and Layout modes are never active
// at once, so a key may be reused across them — uniqueness is enforced PER mode,
// not globally. "both" actions (e.g. the 2D/3D toggle, Select) live in both and
// so reserve their key in either mode.
export type HotkeyMode = "model" | "layout" | "both";

export interface HotkeyDef {
  id: string;
  label: string;
  group: "Tools" | "Shape" | "View" | "Selection" | "Layout" | "Camera";
  mode: HotkeyMode;
  defaultKey: string; // lower-case single char
}

// The rebindable set: the model-mode Tools + Shape keys, plus the global View
// toggle. (Cmd/Ctrl combos and Frame view are shown in the overlay as a fixed
// reference, not rebindable here.)
export const HOTKEY_DEFS: HotkeyDef[] = [
  { id: "tool-create", label: "Create", group: "Tools", mode: "model", defaultKey: "c" },
  // Select/Erase/Wall moved off W/A/S/D/E/Q so those are free for camera movement
  // (WASD planar + E/Q up/down). All of these stay user-rebindable below.
  { id: "tool-erase", label: "Erase", group: "Tools", mode: "model", defaultKey: "t" },
  { id: "tool-select", label: "Select", group: "Tools", mode: "both", defaultKey: "g" },
  { id: "tool-move", label: "Move", group: "Tools", mode: "model", defaultKey: "m" },
  { id: "tool-paint", label: "Paint", group: "Tools", mode: "model", defaultKey: "p" },
  { id: "tool-bucket", label: "Paint Bucket", group: "Tools", mode: "model", defaultKey: "f" },
  { id: "shape-box", label: "Box", group: "Shape", mode: "model", defaultKey: "b" },
  { id: "shape-extrude", label: "Extrude", group: "Shape", mode: "model", defaultKey: "x" },
  { id: "shape-voxel", label: "Voxel", group: "Shape", mode: "model", defaultKey: "v" },
  { id: "shape-circle", label: "Circle", group: "Shape", mode: "model", defaultKey: "o" },
  { id: "shape-freeform", label: "Freeform", group: "Shape", mode: "model", defaultKey: "l" },
  { id: "view-toggle", label: "Toggle 2D / 3D", group: "View", mode: "both", defaultKey: "5" },
  { id: "select-invert", label: "Invert selection", group: "Selection", mode: "model", defaultKey: "i" },
  // Layout-mode tools. Select shares the model-mode "Select" key (it's the same
  // tool in both modes); Wall and Measure are layout-only, so their keys can
  // coincide with model-mode keys without conflict.
  { id: "layout-wall", label: "Wall", group: "Layout", mode: "layout", defaultKey: "h" },
  { id: "layout-measure", label: "Measure", group: "Layout", mode: "layout", defaultKey: "r" },
  // Camera movement (3D perspective views only). These are HELD, not edge-fired —
  // the per-frame mover in main.ts reads the bound keys live each frame — but
  // they live here so the Settings overlay rebinds them like any other shortcut.
  { id: "move-forward", label: "Move forward", group: "Camera", mode: "both", defaultKey: "w" },
  { id: "move-left", label: "Move left", group: "Camera", mode: "both", defaultKey: "a" },
  { id: "move-back", label: "Move back", group: "Camera", mode: "both", defaultKey: "s" },
  { id: "move-right", label: "Move right", group: "Camera", mode: "both", defaultKey: "d" },
  { id: "move-up", label: "Move up", group: "Camera", mode: "both", defaultKey: "e" },
  { id: "move-down", label: "Move down", group: "Camera", mode: "both", defaultKey: "q" },
];

// v3: the camera-move bindings (WASD + E/Q) reshuffled the Select / Erase / Wall
// defaults. An older save is migrated once, but any override that would re-claim
// a movement key is dropped so movement works out of the box. A current-version
// save is trusted as-is (setBinding already enforced uniqueness when it wrote).
const STORAGE_KEY = "tinydraft.hotkeys.v3";
const LEGACY_KEYS = ["tinydraft.hotkeys.v2", "tinydraft.hotkeys"];
const MOVE_KEYS = new Set(["w", "a", "s", "d", "e", "q"]);

// id -> key. Seeded from defaults, then overlaid with any saved overrides.
const bindings: Record<string, string> = {};

function load(): void {
  for (const d of HOTKEY_DEFS) bindings[d.id] = d.defaultKey;
  try {
    let raw = localStorage.getItem(STORAGE_KEY);
    let migrating = false;
    if (!raw) {
      for (const lk of LEGACY_KEYS) {
        const r = localStorage.getItem(lk);
        if (r) {
          raw = r;
          migrating = true;
          break;
        }
      }
    }
    if (raw) {
      const saved = JSON.parse(raw) as Record<string, unknown>;
      for (const d of HOTKEY_DEFS) {
        const k = saved[d.id];
        if (typeof k !== "string") continue;
        if (migrating && MOVE_KEYS.has(k)) continue; // free the movement keys
        bindings[d.id] = k;
      }
    }
  } catch {
    /* corrupt / unavailable storage → just use defaults */
  }
}
load();

function persist(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(bindings));
  } catch {
    /* storage may be full / blocked — bindings still work for this session */
  }
}

// Current key bound to an action ("" if unbound).
export function keyForAction(id: string): string {
  return bindings[id] ?? "";
}

// Two actions contend for a key only if their modes can be active together:
// same mode, or either is "both". (model vs layout never collide.)
function modesContend(a: HotkeyMode, b: HotkeyMode): boolean {
  return a === "both" || b === "both" || a === b;
}

// Action id bound to a pressed (lower-cased) key in the given app mode, or null.
// A key can map to different actions in model vs layout mode.
export function actionForKey(key: string, mode: "model" | "layout"): string | null {
  if (!key) return null;
  for (const d of HOTKEY_DEFS) {
    if (bindings[d.id] === key && modesContend(d.mode, mode)) return d.id;
  }
  return null;
}

// Apply a rebind to a DRAFT map — same uniqueness rule as a live bind (binding a
// key already held by an action in a contending mode unbinds the previous owner;
// an empty key clears it), but with NO persistence and NO effect on the live
// bindings. The Settings overlay edits a draft so nothing takes effect until the
// user clicks Save.
export function draftSetBinding(
  draft: Record<string, string>,
  id: string,
  key: string,
): void {
  key = key.toLowerCase();
  const self = HOTKEY_DEFS.find((d) => d.id === id);
  if (key && self) {
    for (const d of HOTKEY_DEFS) {
      if (d.id !== id && draft[d.id] === key && modesContend(d.mode, self.mode)) {
        draft[d.id] = "";
      }
    }
  }
  draft[id] = key;
}

// A fresh map of every action → its DEFAULT key (the overlay's "Reset" stages
// this into the draft; it only applies once the user Saves).
export function defaultBindings(): Record<string, string> {
  const m: Record<string, string> = {};
  for (const d of HOTKEY_DEFS) m[d.id] = d.defaultKey;
  return m;
}

// A copy of the live bindings, to seed an editable draft.
export function currentBindings(): Record<string, string> {
  return { ...bindings };
}

// Commit an edited draft to the live bindings and persist it (Settings → Save).
export function commitBindings(next: Record<string, string>): void {
  for (const d of HOTKEY_DEFS) {
    if (typeof next[d.id] === "string") bindings[d.id] = next[d.id];
  }
  persist();
}

// Snapshot for the Settings overlay: each def with its current key.
export function allBindings(): { def: HotkeyDef; key: string }[] {
  return HOTKEY_DEFS.map((d) => ({ def: d, key: bindings[d.id] }));
}
